// Direction for the Update Existing import and the Edit dialog.
// transactions.direction is the stored record of which way the money went.
// Income is money in and expense money out; a stale opposite value is never kept.

// 'In' / 'out ' / 'OUT' -> 'in' / 'out'; anything else -> null.
export function parseDirection(value) {
  const v = String(value ?? '').trim().toLowerCase();
  return v === 'in' || v === 'out' ? v : null;
}

// Direction change an Update Existing row proposes, or null. Only an explicit
// Direction column proposes one; an unmapped Direction is never derived from
// a type change (a None to Out with the same money flow is not a change).
export function updatedDirection(original, updated) {
  const target = parseDirection(updated?.direction);
  if (!target) return null;
  return target === String(original?.direction || '').toLowerCase() ? null : target;
}

// 'Transfer' -> 'transfer'; empty -> null.
export function normalizeType(value) {
  const v = String(value ?? '').trim().toLowerCase();
  return v || null;
}

// Direction the row ends with (or null when unknown). Mirrors the server:
// explicit Direction, else the stored direction; a transfer without either
// keeps its money flow from the sign, else the old type.
export function finalDirection(original, updated) {
  const sent = parseDirection(updated?.direction);
  if (sent) return sent;
  const sentType = normalizeType(updated?.transaction_type);
  const type = sentType || normalizeType(original?.transaction_type);
  const stored = parseDirection(original?.direction);
  if (type === 'transfer') {
    if (stored) return stored;
    if (Number(original?.amount) < 0) return 'out';
    if (original?.transaction_type === 'income') return 'in';
    if (original?.transaction_type === 'expense') return 'out';
  }
  return stored;
}

// Amount a row that ends as a transfer is saved with (same rule as the
// server and the Edit dialog): In is positive; Out keeps the row's sign style.
// Returns null when the stored amount stays.
export function transferAmount(original, updated) {
  const type = normalizeType(updated?.transaction_type) || normalizeType(original?.transaction_type);
  const dir = finalDirection(original, updated);
  if (type !== 'transfer' || !dir) return null;
  const sent = updated?.amount;
  const base = sent !== null && sent !== undefined && sent !== '' ? Number(sent) : Number(original?.amount);
  if (!Number.isFinite(base)) return null;
  const abs = Math.abs(base);
  const signed = dir === 'in' ? abs : ((Number(original?.amount) < 0 || base < 0) ? -abs : abs);
  return signed === Number(original?.amount) ? null : signed;
}

// Starting In/Out for the Edit dialog: the sign first, then the type for
// income/expense, then the saved direction.
export function initialDirection(t) {
  if (!t) return '';
  if (Number(t.amount) < 0) return 'out';
  if (t.transaction_type === 'income') return 'in';
  if (t.transaction_type === 'expense') return 'out';
  if (t.transaction_type === 'refund') return parseDirection(t.direction) || 'in';
  return parseDirection(t.direction) || '';
}

// Start value for the dialog's Direction select. Same as Update Existing: a
// saved row keeps its stored direction, even when the Type changes, unless the
// user picks another Direction. A conflicting kept direction is refused by the
// server with its rule A message (never flipped silently). Only a new row or
// an empty stored direction gets the type default.
export function dialogDirection(transaction, type) {
  if (!transaction) return initialDirection({ transaction_type: type });
  const stored = parseDirection(transaction.direction);
  if (stored) return stored;
  return initialDirection({ amount: transaction.amount, transaction_type: type });
}

const label = (v) => v.charAt(0).toUpperCase() + v.slice(1);

// Rule A refusal for a row without a Direction: a type changed to income or
// expense that conflicts with the stored direction (positive income stored
// Out, positive expense stored In, or a negative amount stored In). Mirrors
// the server's bulk-update check. Returns the message, or null.
export function typeDirectionConflict(original, updated) {
  if (parseDirection(updated?.direction)) return null;
  const type = normalizeType(updated?.transaction_type);
  if ((type !== 'income' && type !== 'expense') || type === normalizeType(original?.transaction_type)) return null;
  const stored = parseDirection(original?.direction);
  if (!stored) return null;
  const implied = Number(original?.amount) < 0 ? 'out' : (type === 'income' ? 'in' : 'out');
  if (implied === stored) return null;
  return `Type ${label(type)} conflicts with stored direction ${label(stored)}; add a Direction column or choose Refund`;
}

// Review entry for the Direction field, or null. An explicit Direction column
// is shown as a change; a row changed to Transfer shows the direction it keeps
// when one is stored (never a None to Out with the same money flow).
export function directionChange(original, updated) {
  const show = (d) => (d ? label(d) : 'None');
  const base = { field: 'direction', label: 'Direction', oldValue: original?.direction ?? null, oldDisplay: show(original?.direction), needsAction: false };
  const dir = updatedDirection(original, updated);
  if (dir) return { ...base, newValue: dir, newDisplay: show(dir) };
  const stored = parseDirection(original?.direction);
  if (stored && normalizeType(updated?.transaction_type) === 'transfer' && normalizeType(original?.transaction_type) !== 'transfer') {
    return { ...base, newValue: stored, newDisplay: show(stored) + ' (kept)' };
  }
  return null;
}
